import {
    AppBar,
    styled,
    Typography,
    Toolbar,
    Divider,
    Tooltip,
} from '@mui/material';
import { ComponentProps, ReactElement, useState } from 'react';
import { CSSGrid } from 'src/components/common/CSSGrid';
import { LogoIcon } from 'src/components/common/icons/Logo';
import { HelpLink } from 'src/components/common/HelpLink';
import { Breadcrumbs } from 'src/components/common/Breadcrumbs';
import { Link } from 'react-router-dom';
import { GALLERY_CONFLUENCE_URL } from 'src/constants';
import { useToolContext } from 'src/hooks/useToolContext';
import { ToolDrawer, MenuButton } from './ToolDrawer';

export type PropTypes = Omit<ComponentProps<typeof AppBar>, 'title'> & {
    title?: ReactElement | string;
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    gridArea: 'header',
    minHeight: theme.spacing(16),
    paddingLeft: theme.spacing(3),
    paddingTop: theme.spacing(2),
    zIndex: 500,
}));

const StyledCssGrid = styled(CSSGrid)(({ theme }) => ({
    width: '100%',
    gridAutoFlow: 'row',
    gridTemplateAreas: '". . . . . ."',
    gridTemplateColumns: 'auto auto 1fr auto auto',
    alignItems: 'center',
    gap: theme.spacing(4),
    paddingBottom: theme.spacing(2),
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightRegular,
}));

const StyledToolbar = styled(Toolbar)({
    display: 'grid',
    gridAutoFlow: 'row',
});

const helpHref = `${GALLERY_CONFLUENCE_URL}DCI/pages/5196139285/Design+Relevance+Tooling+Documentation`;

export const TitledAppBar = (props: PropTypes): JSX.Element => {
    const { children, title, ...rest } = props;
    const {
        currentTool, tooltip, showBreadcrumbs,
    } = useToolContext();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const handleDrawerToggle = (): void => {
        setDrawerOpen(!drawerOpen);
    };

    return (
        <>
            <StyledAppBar color="inherit" position="sticky" {...rest}>
                <StyledToolbar>
                    <StyledCssGrid gap={3}>
                        <MenuButton onClick={handleDrawerToggle} />
                        <Tooltip title={tooltip}>
                            <Link to={currentTool}>
                                <LogoIcon size="sm" />
                            </Link>
                        </Tooltip>
                        <StyledTypography variant="h6">
                            {title}
                        </StyledTypography>
                        {children}
                        <HelpLink href={helpHref} />
                    </StyledCssGrid>
                    {showBreadcrumbs && (
                        <>
                            <Divider />
                            <Breadcrumbs />
                        </>
                    )}
                </StyledToolbar>
            </StyledAppBar>
            <ToolDrawer
                open={drawerOpen}
                onClose={(): void => setDrawerOpen(false)}
            />
        </>
    );
};
